import { ARCHETYPES, ARCHETYPE_KEYS, MAX_SCORES } from '../data/archetypes'
import { QUESTIONS } from '../data/questions'
import type { AnswerMap, ArchetypeKey, ArchetypeResult } from '../types/assessment'

/**
 * ============================================================
 * SCORING — the single source of truth
 * ------------------------------------------------------------
 * 1. rawScores()     unchanged from the original assessment:
 *                    answer value (0-4) x question weight, summed.
 * 2. matchPercents() raw / MAX_SCORES, the existing "% match".
 * 3. buildProfile()  normalizes the match percentages so all eight
 *                    total exactly 100 for the profile display.
 *
 * The underlying weights are NOT modified to produce percentages.
 * Normalization is applied on top of the existing result, so the
 * ordering (and therefore the primary archetype) is identical to
 * the original assessment.
 * ============================================================
 */

export function rawScores(answers: AnswerMap): Record<ArchetypeKey, number> {
  const raw = Object.fromEntries(ARCHETYPE_KEYS.map((k) => [k, 0])) as Record<ArchetypeKey, number>

  for (const question of QUESTIONS) {
    const value = answers[question.id] ?? 0
    for (const [key, weight] of Object.entries(question.weights)) {
      raw[key as ArchetypeKey] += value * (weight ?? 0)
    }
  }

  return raw
}

export function matchPercents(answers: AnswerMap): Record<ArchetypeKey, number> {
  const raw = rawScores(answers)
  return Object.fromEntries(
    ARCHETYPE_KEYS.map((key) => [key, Math.round((raw[key] / MAX_SCORES[key]) * 100)]),
  ) as Record<ArchetypeKey, number>
}

/**
 * Distributes 100 points across the eight archetypes in proportion to their
 * match scores, using the largest-remainder method so the displayed values
 * always total exactly 100 with no rounding drift.
 */
function normalizeToHundred(values: Record<ArchetypeKey, number>): Record<ArchetypeKey, number> {
  const total = ARCHETYPE_KEYS.reduce((sum, key) => sum + values[key], 0)

  if (total <= 0) {
    // No signal at all: distribute evenly rather than show eight zeros.
    const even = Math.floor(100 / ARCHETYPE_KEYS.length)
    const result = Object.fromEntries(ARCHETYPE_KEYS.map((k) => [k, even])) as Record<ArchetypeKey, number>
    result[ARCHETYPE_KEYS[0]] += 100 - even * ARCHETYPE_KEYS.length
    return result
  }

  const exact = ARCHETYPE_KEYS.map((key) => ({ key, value: (values[key] / total) * 100 }))
  const floored = exact.map((entry) => ({ ...entry, floor: Math.floor(entry.value) }))
  const assigned = floored.reduce((sum, entry) => sum + entry.floor, 0)

  const remainderOrder = [...floored].sort(
    (a, b) => b.value - b.floor - (a.value - a.floor) || b.value - a.value,
  )

  const result = Object.fromEntries(floored.map((e) => [e.key, e.floor])) as Record<ArchetypeKey, number>
  for (let i = 0; i < 100 - assigned; i += 1) {
    result[remainderOrder[i % remainderOrder.length].key] += 1
  }

  return result
}

/**
 * The complete profile: all eight archetypes, sorted highest to lowest.
 * `results[0]` is the primary archetype, `results[1]` the secondary.
 */
export function buildProfile(answers: AnswerMap): ArchetypeResult[] {
  const match = matchPercents(answers)
  const profile = normalizeToHundred(match)

  return ARCHETYPE_KEYS.map((key) => ({
    key,
    name: ARCHETYPES[key].name,
    matchPct: match[key],
    profilePct: profile[key],
  })).sort((a, b) => b.matchPct - a.matchPct || b.profilePct - a.profilePct)
}
